<template>
    <cr-confirmation-dialog v-model="open" cancelBackgroundColor="cr-primary-button-color" cancelTextColor="cr-primary-button-text-color" :title="t('REPORT_CARD_DIALOG_TITLE')" :confirm="false" :cancel-text="t('REPORT_CARD_DIALOG_CLOSE')" data-testing="report-card-dialog">
        <div v-if="loading" class="report-card-dialog-loading-wrapper">
            <cr-loading :message="t('REPORT_CARD_DIALOG_LOADING_MESSAGE')" data-testing="report-card-dialog-loading"/>
        </div>
        <template v-else-if="reportCard">
            <dl class="report-card-dialog-header" data-testing="report-card-dialog-header">
                <div>
                    <dt>{{ t('REPORT_CARD_DIALOG_NAME_LABEL') }}</dt>
                    <dd data-testing="report-card-dialog-player-name">{{ playerName }}</dd>
                </div>

                <div>
                    <dt>{{ t('REPORT_CARD_DIALOG_GENERATED_ON_LABEL') }}</dt>
                    <dd data-testing="report-card-dialog-created">{{ createdDisplay }}</dd>
                </div>

                <div style="display: flex;">
                    <dt>{{ t('REPORT_CARD_DIALOG_SHARABLE_LINK_LABEL') }}</dt>
                    <dd>
                        <cr-external-link :href="reportCard.shortLink" data-testing="report-card-dialog-short-link">{{ reportCard.shortLink }}</cr-external-link>
                        <cr-clipboard-copy :value="reportCard.shortLink" :copy-label="t('REPORT_CARD_DIALOG_COPY_LABEL')" data-testing="report-card-dialog-copy"/>
                        <div class="report-card-dialog-expiry" data-testing="report-card-dialog-expiry">
                            {{ t('REPORT_CARD_DIALOG_LINK_VALID_UNTIL', { date: expiresDisplay }) }}
                        </div>
                    </dd>
                </div>
            </dl>

            <cr-table :headers="tableHeaders" :items="tableRows" data-testing="report-card-dialog-table"/>
        </template>
    </cr-confirmation-dialog>
</template>

<style scoped>
a {
    color: var(--cr-primary-button-color);
}
.cr-clipboard-copy {
    --cr-clipboard-copy-message: var(--cr-primary-button-color);
}

.report-card-dialog-loading-wrapper
{
    display: flex;
    justify-content: center;
}

.report-card-dialog-header dt,
.report-card-dialog-header dd {
    display: inline-block;
}

.report-card-dialog-header dt
{
    font-weight: bold;
    margin-right: 1ch;
}

.report-card-dialog-header dd
{
    margin-left: 0;
    margin-bottom: 0.75rem;
}

.report-card-dialog-expiry
{
    color: var(--cr-grey);
}

:deep(.cr-table) {
    border-collapse: collapse;
}
:deep(th) {
    background-color: var(--cr-table-header-background-color);
}
:deep(td) {
    background-color: unset;
}

:deep(th), :deep(td) {
    color: var(--cr-text);
    border: 1px solid var(--cr-table-border-color);
}
</style>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { IApiClientError } from '@cyber-range/cyber-range-api-client';
import ChallengeCategoryPrefix from '../../interfaces/challengeCategoryPrefix';
import { useReportCardStore } from '../../stores/reportCardStore';
import { usePlayerStore } from '../../stores/playerStore';
import { useErrorStore } from '../../stores/errorStore';
import { useLevelStore } from '../../stores/levelStore';

const { t } = useI18n();

const open = defineModel<boolean>();

const props = defineProps<{ reportCardId?: string }>();

const loading = ref(false);

const reportCardStore = useReportCardStore();
const { reportCard, reportCardId } = storeToRefs(reportCardStore);

const playerName = computed(() => reportCard.value?.playerName ?? '');

const createdDisplay = computed(() =>
    reportCard.value
        ? new Date(reportCard.value.createdTimestamp).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : ''
);

const expiresDisplay = computed(() =>
    reportCard.value
        ? new Date(reportCard.value.expiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : ''
);

const tableHeaders = computed(() => [
    { key: 'level', text: t('REPORT_CARD_DIALOG_COLUMN_LEVEL') },
    { key: 'points', text: t('REPORT_CARD_DIALOG_COLUMN_POINTS'), alignHeader: 'center', alignCell: 'right' },
    { key: 'status', text: t('REPORT_CARD_DIALOG_COLUMN_STATUS'), align: 'center' }
]);

const tableRows = computed(() =>
    useLevelStore().levels.map(level =>
    {
        const key = `${ChallengeCategoryPrefix.LevelPick}${level.categorySuffix}`;
        const points = reportCard.value?.scoreByCategory?.[key] ?? 0;
        const completed = points >= level.completionThreshold;

        return {
            level: `${level.namePrefix} - ${level.nameSuffix}`,
            points,
            status: completed ? t('REPORT_CARD_DIALOG_STATUS_COMPLETED') : ''
        };
    })
);

watch(() => open.value, async (isOpen) =>
{
    if (!isOpen)
    {
        return;
    }

    loading.value = true;

    let idToFetch = props.reportCardId ?? reportCardId.value;

    if (!idToFetch)
    {
        const playerId = usePlayerStore().getCurrentPlayer?.id;

        if (!playerId)
        {
            reportCardStore.reportCard = undefined;
            useErrorStore().setError({ statusCode: 404, message: t('REPORT_CARD_DIALOG_NO_PLAYER_ERROR'), details: '' } as IApiClientError);
            loading.value = false;
            return;
        }

        idToFetch = await reportCardStore.createReportCard(playerId);
    }

    await reportCardStore.fetchReportCard(idToFetch);

    loading.value = false;
}, { immediate: true });
</script>
